import { useApiMutation, useApiQuery } from "@gladis/api-client";
import { formatEur, type MemberDto, type SavingsAccountsDto } from "@gladis/shared";
import { ActionMenu, EmptyState, ErrorState, LoadingState, PersonLabel, Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * The prefix EVERY savings mutation invalidates.
 *
 * Not the accounts key alone. The list, the chart and each account's history are three queries over
 * one set of facts, and a mutation changes all of them: adding an account changes the chart, and
 * recording a balance changes the list. Invalidating only the key a component happens to own leaves
 * the others showing the state before the write — which is how the chart sat on its empty state
 * while two accounts were plainly listed underneath it, caught by the end-to-end journey.
 *
 * TanStack matches query keys by prefix, so this one covers all three.
 */
export const SAVINGS_KEY = ["savings"] as const;

/** The accounts query itself. Read by the list and the archived disclosure. */
export const SAVINGS_ACCOUNTS_KEY = ["savings", "accounts"] as const;

export function useSavingsAccounts(): ReturnType<typeof useApiQuery<SavingsAccountsDto>> {
  return useApiQuery<SavingsAccountsDto>(SAVINGS_ACCOUNTS_KEY, "/savings/accounts");
}

/**
 * The household's savings: one row per active account.
 *
 * Co-located in the savings package rather than in `apps/app`, because it knows what a savings
 * account is. `apps/app` composes it; it does not own it.
 *
 * EVERY FIGURE GOES THROUGH `formatEur`. Not a style preference — there is a test in
 * `packages/shared` that greps the repository for a second currency formatter and fails if one
 * appears, because "€ 1.234,00" on one screen and "€1,234.00" on another is the kind of bug nobody
 * files and everybody notices.
 *
 * The ownership label is NOT assembled here. The server sends `ownerLabel` already resolved — the
 * owner's current name while they are a member, the name snapshotted on the account once they are
 * not (FR-024) — so this component cannot get the fallback wrong, because it never sees the choice.
 *
 * NO ADD CONTROL IN HERE. "Add a savings account" is the page's primary action and lives at the end
 * of its title row, where `Page.Title` puts every page's — so it is in the same place whether the
 * household has no accounts or nine, rather than moving into the empty state when the list is empty.
 */
export function AccountList({
  onSelect,
  onOpen,
  members = [],
}: {
  /** Called with the account a member wants to update. The page owns what happens next. */
  onSelect?: (account: { id: string; name: string }) => void;
  /** Called with the id of the account whose history a member wants to open. */
  onOpen?: (accountId: string) => void;
  /**
   * The household's members, for their faces (020).
   *
   * PASSED IN RATHER THAN FETCHED, because a domain `/ui` package may not import another one and
   * the member list belongs to identity. `apps/app` already holds it for this page, so this costs
   * no request — and defaulting to `[]` means every face degrades to initials rather than the
   * component failing when a caller has not wired it yet.
   */
  members?: readonly MemberDto[];
}): ReactNode {
  const t = useT();
  const accounts = useSavingsAccounts();

  if (accounts.isPending) return <LoadingState>{t("savings.list.loading")}</LoadingState>;
  if (accounts.isError) return <ErrorState>{t("savings.list.loadFailed")}</ErrorState>;

  const { active } = accounts.data;

  if (active.length === 0) {
    return (
      <EmptyState>
        <p>{t("savings.list.emptyTitle")}</p>
        <p className="mt-2 text-sm">{t("savings.list.emptyHint")}</p>
      </EmptyState>
    );
  }

  return (
    /*
     * THE TOTAL AND THE BREAKDOWN ARE NOT HERE ANY MORE. They are the page's headline figures, and they now sit
     * in the stat row at the top of it (`SavingsStats`) — where they are in the same place whether a form is
     * open above this list or not, and where a household with no accounts still sees them.
     *
     * `aria-labelledby` went with them, so this section carries its own heading instead: it used to be labelled
     * by the total, which named the section "€ 30.030,00" to a screen reader.
     */
    <section aria-labelledby="savings-accounts-heading">
      <h2 id="savings-accounts-heading" className="mb-4 text-xl font-semibold">
        {t("savings.list.heading")}
      </h2>

      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>{t("savings.list.colAccount")}</Table.HeaderCell>
            <Table.HeaderCell>{t("savings.list.colHeldBy")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("savings.list.colBalance")}</Table.HeaderCell>
            <Table.HeaderCell> </Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {active.map((account) => (
            <Table.Row key={account.id}>
              <Table.Cell>
                {onOpen ? (
                  <button type="button" className="underline" onClick={() => onOpen(account.id)}>
                    {account.name}
                  </button>
                ) : (
                  account.name
                )}
              </Table.Cell>
              {/*
               * "Joint" is the label for a joint account, not an absence. `ownerLabel` is null for
               * one, and rendering an empty cell would read as missing data rather than as the
               * account belonging to the household as a whole.
               *
               * `isPerson` carries that distinction to the avatar: a joint account gets no mark,
               * because there is nobody to depict. A departed owner keeps their snapshotted name
               * and loses their face, which is the lookup below simply missing (FR-022).
               */}
              <Table.Cell>
                <PersonLabel
                  label={account.ownerLabel ?? t("person.joint")}
                  isPerson={account.ownerLabel !== null}
                  avatarUrl={members.find((m) => m.userId === account.ownerId)?.avatarUrl}
                  // A joint account belongs to everybody, so it shows everybody. `group` is read
                  // only when `isPerson` is false, so this is inert on an individual row.
                  group={members.map((m) => ({ name: m.displayName, avatarUrl: m.avatarUrl }))}
                />
              </Table.Cell>
              <Table.Cell numeric testId={`balance-${account.id}`}>
                {formatEur(account.currentBalance)}
              </Table.Cell>
              <Table.Cell>
                <RowActions account={account} onSelect={onSelect} />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </section>
  );
}

/**
 * One trigger for the row's actions, as every other table in the application has.
 *
 * TWO TEXT BUTTONS UNTIL 2026-08-14 — "Update balance" and "Archive" — which made the last column as wide
 * as both labels together and put the row's controls in competition with its BALANCE, the figure anyone
 * opens this page to read. Expenses, income's sources, a property's mortgage parts and a stock had all
 * been consolidated already; this table was the one left behind.
 *
 * ITS OWN COMPONENT SO THE MUTATION HOOK IS PER-ROW. It was one `useApiMutation` in `AccountList`, shared
 * by every row, which is the shape that produces a real bug rather than an untidy one: `isPending` belongs
 * to whichever row was clicked last, so two rapid archives leave the second row's trigger enabled and the
 * first one's disabled. Moving the hook here means a row's pending state is its own.
 *
 * The labels say WHAT WILL HAPPEN rather than what the row is — "Archive this account", not "Archived".
 * And the trigger is named after the account, because six triggers all reading "Actions" are six identical
 * accessible names in one table.
 */
function RowActions({
  account,
  onSelect,
}: {
  account: { id: string; name: string };
  onSelect: ((account: { id: string; name: string }) => void) | undefined;
}): ReactNode {
  const t = useT();
  const archive = useApiMutation<void, { accountId: string }>(
    (client, input) => client.post<void>(`/savings/accounts/${input.accountId}/archive`, {}),
    { invalidates: [SAVINGS_KEY] },
  );

  return (
    <ActionMenu
      label={`Actions for ${account.name}`}
      items={[
        ...(onSelect === undefined
          ? []
          : [
              {
                label: t("savings.list.updateBalance"),
                onSelect: () => {
                  onSelect({ id: account.id, name: account.name });
                },
              },
            ]),
        {
          label: t("savings.list.archive"),
          // Archiving withdraws an account from every total. Restoring, in `ArchivedAccounts`, puts one
          // back — so only this direction is the set's removing action.
          destructive: true,
          disabled: archive.isPending,
          onSelect: () => {
            archive.mutate({ accountId: account.id });
          },
        },
      ]}
    />
  );
}
